import type { ChangeEvent, FC } from 'react';
import React, { useEffect, useMemo, useCallback, useState } from 'react';

import { useAppContext } from '../../contexts';
import { useTranslations, useUserData } from '../../hooks';
import { BackInStockNotificationChannel } from '../../models';
import { Loading } from '../Loading';
import { NotifyForm } from '../NotifyForm';
import { NotifyType } from '../NotifyType';
import { RequestFailed } from '../RequestFailed';
import { Thanks } from '../Thanks';
import { Header, InfoText, Root } from './styles';

export const NotifyCumulus: FC = () => {
  const { config, productCode, successClose } = useAppContext();
  const { translations } = useTranslations();
  const [currentType, setCurrentType] = useState(
    config?.isSmsChannelDefault ? BackInStockNotificationChannel.Sms : BackInStockNotificationChannel.Email,
  );
  const [inputData, setInputData] = useState('');
  const [isReserved, setIsReserved] = useState(false);
  const { userData, loading, error } = useUserData({ currentType, setInputData });
  const isEmail: boolean = BackInStockNotificationChannel.Email === currentType;

  useEffect(() => {
    if (userData.email && currentType === BackInStockNotificationChannel.Email) {
      setInputData(userData.email);
    } else if (userData.telephoneNumber && currentType === BackInStockNotificationChannel.Sms) {
      setInputData(userData.telephoneNumber);
    }
  }, [currentType, userData.email, userData.telephoneNumber]);

  const onChangeData = useCallback((event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const trimmedValue = event.currentTarget.value.trim();
    setInputData(trimmedValue);
  }, []);

  const onChangeType = useCallback((selectedType: BackInStockNotificationChannel): void => {
    setCurrentType(selectedType);
  }, []);

  const label = useMemo(
    () => (isEmail ? translations.enterYourEmail : translations.enterTelephoneNumber),
    [isEmail, translations.enterTelephoneNumber, translations.enterYourEmail],
  );

  const onSubmit = useCallback((): void => {
    setIsReserved(true);
    successClose();
  }, [successClose]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return (
      <Root>
        <RequestFailed />
      </Root>
    );
  }

  return (
    <Root isSmsEnabled={config?.smsSubscriptionEnabled}>
      {isReserved ? (
        <Thanks currentType={currentType} />
      ) : (
        <>
          <Header variant="h5">{translations.notifyMe}</Header>
          <InfoText variant="body1">{label}</InfoText>
          <NotifyType
            currentType={currentType}
            onChange={onChangeType}
          />
          <NotifyForm
            currentType={currentType}
            inputData={inputData}
            productCode={productCode}
            onChange={onChangeData}
            onSubmit={onSubmit}
          />
        </>
      )}
    </Root>
  );
};
